import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Camera, ImagePlus, CheckCircle2, Loader2, Smartphone } from "lucide-react";
import api, { formatError } from "@/lib/api";
import logoBlack from "@/assets/logo-black.png";

const MAX_DIM = 1280;
const JPEG_QUALITY = 0.7;

function compressImage(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      if (width > height && width > MAX_DIM) { height = Math.round(height * MAX_DIM / width); width = MAX_DIM; }
      else if (height >= width && height > MAX_DIM) { width = Math.round(width * MAX_DIM / height); height = MAX_DIM; }
      const canvas = document.createElement("canvas");
      canvas.width = width; canvas.height = height;
      canvas.getContext("2d").drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL("image/jpeg", JPEG_QUALITY));
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Immagine non leggibile")); };
    img.src = url;
  });
}

export default function MobileUpload() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [items, setItems] = useState([]); // {id, thumb, status}
  const [uploading, setUploading] = useState(0);
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/public/upload/${token}`); setInfo(data); }
    catch (e) { setError(formatError(e.response?.data?.detail)); }
  }, [token]);

  useEffect(() => { if (token) load(); }, [token, load]);

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f.type.startsWith("image/"));
    if (!files.length) return;
    setUploading((n) => n + files.length);
    for (const file of files) {
      const localId = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      try {
        const dataUrl = await compressImage(file);
        setItems((prev) => [{ id: localId, thumb: dataUrl, status: "uploading" }, ...prev]);
        await api.post(`/public/upload/${token}/photo`, { url: dataUrl, category: "INTAKE" });
        setItems((prev) => prev.map((it) => it.id === localId ? { ...it, status: "done" } : it));
        setInfo((i) => i ? { ...i, photo_count: (i.photo_count || 0) + 1 } : i);
      } catch (e) {
        setItems((prev) => prev.map((it) => it.id === localId ? { ...it, status: "error" } : it));
        toast.error(formatError(e.response?.data?.detail) || "Caricamento non riuscito");
      } finally {
        setUploading((n) => Math.max(0, n - 1));
      }
    }
  };

  if (error) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-slate-50 p-6 text-center">
        <Smartphone className="mb-3 h-10 w-10 text-slate-400" />
        <h1 className="text-lg font-semibold text-slate-900">Sessione non valida</h1>
        <p className="mt-1 text-sm text-slate-500" data-testid="upload-error">{error}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      <header className="border-b border-slate-200 bg-white px-5 py-4">
        <img src={logoBlack} alt="Dexter Lab" className="h-7 w-auto object-contain" />
      </header>

      <main className="mx-auto max-w-md px-5 py-6">
        {!info ? (
          <div className="flex items-center gap-2 text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Caricamento…</div>
        ) : (
          <>
            <h1 className="text-xl font-semibold text-slate-900">Foto dispositivo</h1>
            <p className="mt-1 text-sm text-slate-600" data-testid="upload-device">
              {info.device.brand} {info.device.model} · {info.repair_number}
            </p>
            {info.customer_name ? <p className="text-sm text-slate-500">{info.customer_name}</p> : null}

            <div className="mt-6 grid grid-cols-2 gap-3">
              <button
                type="button"
                data-testid="upload-camera-btn"
                onClick={() => cameraRef.current?.click()}
                className="flex flex-col items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white py-6 text-slate-800 shadow-sm active:scale-[0.98]"
              >
                <Camera className="h-7 w-7 text-[#0071e3]" />
                <span className="text-sm font-medium">Scatta foto</span>
              </button>
              <button
                type="button"
                data-testid="upload-gallery-btn"
                onClick={() => galleryRef.current?.click()}
                className="flex flex-col items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white py-6 text-slate-800 shadow-sm active:scale-[0.98]"
              >
                <ImagePlus className="h-7 w-7 text-[#0071e3]" />
                <span className="text-sm font-medium">Dalla galleria</span>
              </button>
            </div>

            <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden"
              data-testid="upload-camera-input" onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }} />
            <input ref={galleryRef} type="file" accept="image/*" multiple className="hidden"
              data-testid="upload-gallery-input" onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }} />

            <div className="mt-6 flex items-center justify-between text-sm text-slate-600">
              <span data-testid="upload-count">{info.photo_count || 0} foto caricate</span>
              {uploading > 0 && <span className="flex items-center gap-1 text-[#0071e3]"><Loader2 className="h-4 w-4 animate-spin" /> Invio…</span>}
            </div>

            {items.length > 0 && (
              <div className="mt-3 grid grid-cols-3 gap-2" data-testid="upload-thumbs">
                {items.map((it) => (
                  <div key={it.id} className="relative aspect-square overflow-hidden rounded-xl border border-slate-200 bg-white">
                    <img src={it.thumb} alt="" className="h-full w-full object-cover" />
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                      {it.status === "uploading" && <Loader2 className="h-5 w-5 animate-spin text-white" />}
                      {it.status === "done" && <CheckCircle2 className="h-5 w-5 text-emerald-400" />}
                      {it.status === "error" && <span className="text-xs font-semibold text-red-300">Errore</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <p className="mt-6 text-center text-xs text-slate-400">
              Le foto vengono aggiunte automaticamente alla scheda di riparazione.
            </p>
          </>
        )}
      </main>
    </div>
  );
}
